import React, { useEffect } from "react";
import _ from "lodash";
import { ButtonToggle, ButtonToggleOption, Text, Tooltip } from "@showitinc/showit-wc";

import AppConstants from "../../../../constants/AppConstants";
import TransformerStore from "../../../../stores/TransformerStore";
import TransformActions from "../../../../actions/TransformActions";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import { LINE_THICKNESS } from "../../../../constants/MergeConstants";
import { getProp } from "../../../../utils/Merge/MergeUtils.mjs";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import ElementPropertiesPositionPanel from "../ElementPropertiesPositionPanel";
import usePosition from "../../../../hooks/usePosition";
import SizeAndPositionInputs from "./SizeAndPositionInputs";
import ElementUtils from "@/utils/ElementUtils";
import UIActions from "@/actions/UIActions";

import Styles from "./SizeAndPosition.module.css";

const {
  ALIGN_CENTER,
  ALIGN_MIDDLE,
  ALIGN_TOP,
  ALIGN_BOTTOM,
  ALIGN_LEFT,
  ALIGN_RIGHT,
  DECIMAL_PRECISION,
  ELEMENT_TYPE_SIMPLE,
} = AppConstants;

const formatToPosition = (elem, layout) => {
  const props = layout ? elem.get(layout) : elem;
  const pickable = ["w", "x", "y", "a"];

  return props
    .filter((_1, key) => pickable.includes(key))
    .set("h", layout ? getProp(elem, layout, LINE_THICKNESS) : props.get("h"));
};

const ElementPropertiesSize = (props) => {
  const { element, elementId, isLocked, layout, onPropUpdate, theme } = props;

  const [position, setPosition] = usePosition(element, layout, formatToPosition);
  const type = element.get("type", ELEMENT_TYPE_SIMPLE);

  const _onTransformerChange = () => {
    const positions = TransformerStore.getElementPosition(elementId, true);
    if (!positions) return;

    setPosition(formatToPosition(positions));
  };

  useEffect(() => {
    const unsubscribe = TransformerStore.listen(_onTransformerChange);
    return () => unsubscribe();
  }, [elementId]);

  useEffect(() => setPosition(element, layout), [element, layout]);

  const _onPropChange = (propInfo, e) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const _alignToCanvas = (e) => {
    const alignPos = e?.detail?.value;
    alignPos && TransformActions.alignToCanvas(alignPos);
  };

  const { getPropName } = elementSyncUtils(element, _onPropChange, layout);

  const _onChange = (propInfo) => {
    propInfo.value =
      typeof propInfo.value === "number"
        ? _.round(propInfo.value, DECIMAL_PRECISION)
        : propInfo.value;

    const adjustedPropInfo = ElementUtils.adjustSizeToMinIfNeeded(element, theme, propInfo);

    _onPropChange(adjustedPropInfo);
  };

  const { onChange } = initFormatter(_onChange, getPropName);

  const positionWithMinimums = position
    .set("minHeight", ElementUtils.getMinHeight(element, layout, theme))
    .set("minWidth", ElementUtils.getMinWidth(element, layout, theme));

  const containerId = element.get("pId");
  const onBlur = containerId ? () => UIActions.resizeGroup(containerId) : undefined;

  return (
    <>
      <SplitMergeWrapper layout={layout}>
        <SizeAndPositionInputs
          onChange={onChange}
          locked={isLocked}
          position={positionWithMinimums}
          onBlur={onBlur}
          type={type}
        />
      </SplitMergeWrapper>

      {!containerId && (
        <>
          <Text className="panel-subheading">Align to Canvas</Text>
          <ButtonToggle
            className={Styles.alignmentToggles}
            label="Align to Canvas"
            clickOnly
            onSIChange={_alignToCanvas}
            hideLabel
            disabled={isLocked}
          >
            <Tooltip>
              <span slot="content">
                <Text>Align Left</Text>
              </span>
              <ButtonToggleOption
                className={Styles.alignLeft}
                data-testid="align-to-canvas-left-button"
                icon="alignCanvasLeft"
                value={ALIGN_LEFT}
                clickOnly
                disabled={isLocked}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Center</Text>
              </span>
              <ButtonToggleOption
                className={Styles.alignCenter}
                data-testid="center-to-canvas-horizontal-button"
                icon="alignCanvasHorizCenter"
                value={ALIGN_CENTER}
                clickOnly
                disabled={isLocked}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Right</Text>
              </span>
              <ButtonToggleOption
                className={Styles.alignRight}
                data-testid="align-to-canvas-right-button"
                icon="alignCanvasRight"
                value={ALIGN_RIGHT}
                clickOnly
                disabled={isLocked}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Top</Text>
              </span>
              <ButtonToggleOption
                className={Styles.alignTop}
                data-testid="align-to-canvas-top-button"
                icon="alignCanvasTop"
                value={ALIGN_TOP}
                clickOnly
                disabled={isLocked}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Middle</Text>
              </span>
              <ButtonToggleOption
                className={Styles.alignMiddle}
                data-testid="center-to-canvas-vertical-button"
                icon="alignCanvasVertCenter"
                value={ALIGN_MIDDLE}
                clickOnly
                disabled={isLocked}
              />
            </Tooltip>
            <Tooltip>
              <span slot="content">
                <Text>Align Bottom</Text>
              </span>
              <ButtonToggleOption
                className={Styles.alignBottom}
                data-testid="align-to-canvas-bottom-button"
                icon="alignCanvasBottom"
                value={ALIGN_BOTTOM}
                clickOnly
                disabled={isLocked}
              />
            </Tooltip>
          </ButtonToggle>
          <ElementPropertiesPositionPanel disabled={isLocked} />
        </>
      )}
    </>
  );
};

export default ElementPropertiesSize;
